import React, { useState, useEffect } from 'react';
import { Box } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import BidRequestCreatingProgress from 'sharedComponents/BidRequestCreatingProgress';
import useChats from 'reducers/hooks/useChats';
import BuyerRequestPage from 'sharedComponents/BuyerRequestPage';
import Loader from 'sharedComponents/Loader';
import Seo from 'sharedComponents/Seo';
import { createBidRequestEvent } from 'utils/gtmHelper';

import useFirebaseChat from 'reducers/hooks/firebase/useChat';

import MortgageSurveyPresenter, { buildSurveyValues } from 'presenters/MortgageSurveyPresenter';
import MyBundlePresenter from 'presenters/MyBundlePresenter';

import useBidRequest from 'reducers/hooks/useBidRequest';
import useMyBundle from 'reducers/hooks/useMyBundle';
import useMortgageSurvey from 'reducers/hooks/useMortgageSurvey';
import { appRoutes } from 'routes';

import Survey from './components/Survey';

import useStyles from './useStyles';

const SurveyPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();

  const [initialized, setInitialized] = useState(false);

  const { loadChats } = useChats();
  const { loadMyBundle, myBundle } = useMyBundle();
  const {
    saveAndOpenBidRequest,
    saveBidRequest,
    progress,
    saving,
    creating,
    bidRequest,
    loadBidRequest,
    openBidRequest,
  } = useBidRequest();
  const {
    discardSurveyValues,
    surveyPassed,
    setSurveyPassed,
    surveyValues,
    setInitialSurveyValues,
    surveyChanged,
    setSelectedMortageSurveyFlow,
    selectedMortgageSurveyFlow,
  } = useMortgageSurvey();

  const { setNeedToSelectChat } = useFirebaseChat();

  useEffect(() => {
    const initialize = async () => {
      const { attributes: myBundleAttributes } = await loadMyBundle();
      if (MyBundlePresenter.hasBidRequest(myBundleAttributes)) {
        const { attributes } = await loadBidRequest();
        const values = buildSurveyValues(attributes);
        setSelectedMortageSurveyFlow(MortgageSurveyPresenter.purpose(values));
        setInitialSurveyValues(values);
        setSurveyPassed(true);
      }
      setInitialized(true);
    };
    initialize();

    return () => {
      discardSurveyValues();
    };
  }, []);

  if (!initialized) return <Loader fullFill />;

  const handleRetakeSurvey = () => {
    setSurveyPassed(false);
  };

  const handleCreateRequest = async () => {
    createBidRequestEvent();
    const attrs = MortgageSurveyPresenter.buildBidRequestAtrributes(surveyValues);
    discardSurveyValues();
    await saveAndOpenBidRequest(attrs);
    loadChats();
    setNeedToSelectChat(true);
    history.push(appRoutes.messagesPath());
  };

  const saveBidRequestChanges = async () => {
    const attrs = MortgageSurveyPresenter.buildBidRequestAtrributes(surveyValues);
    await saveBidRequest(attrs);
    await openBidRequest({ id: bidRequest.id, type: 'favorite_los_only' });
    history.push(appRoutes.messagesPath());
  };

  const hasBidRequest = MyBundlePresenter.hasBidRequest(myBundle);

  const handleSubmit = async () => {
    if (hasBidRequest) {
      await saveBidRequestChanges();
    } else {
      await handleCreateRequest();
    }
    discardSurveyValues();
  };

  if (creating) {
    return (
      <Box width="100%" height="100%">
        <BidRequestCreatingProgress progress={progress} />
      </Box>
    );
  }

  if (surveyPassed) {
    return (
      <Box width="100%" height="100%">
        <Seo title={t('meta.SurveyPage.title')} />
        <BuyerRequestPage
          surveyValues={surveyValues}
          onRetake={handleRetakeSurvey}
          onSubmit={handleSubmit}
          submitButtonLabel="Submit"
          disabled={!surveyChanged || saving || creating}
        />
      </Box>
    );
  }

  return (
    <Box width="100%" height="100%" display="flex" flexDirection="column" alignItems="center">
      <Seo title={t('meta.SurveyPage.title')} />
      <Box className={classes.paper}>
        <Box className={classes.survey}>
          <Survey
            onSelectFlow={setSelectedMortageSurveyFlow}
            selectedFlow={selectedMortgageSurveyFlow}
            surveyValues={surveyValues}
            setInitialSurveyValues={setInitialSurveyValues}
            hasBidRequest={hasBidRequest}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SurveyPage;



// WEBPACK FOOTER //
// ./src/components/SurveyPage/SurveyPage.js